/**
 * Symphony GROUP CHAT — the caller's own NOTIFICATION MODE for one group, on web. iOS `GroupNotifications.swift` and
 * Android `GroupNotifications.kt` hold the same rules, so the picker, the row value and the one-time hint cannot drift.
 *
 * Product: without your Maestro in the group you get every message (unless you pick "Mentions only"); with your
 * Maestro in it, the Maestro decides when to notify you. Mentions and replies to you always notify, even when muted.
 * The service resolves the DEFAULT on every read (Maestro decides while your own Maestro is in the group, else all
 * messages) and keeps an explicit choice; so `explicit` false means "the mode shown is the automatic one".
 *
 * Wire (common-lib `groupNotificationsWire`): `{ mode, explicit, muted, maestroDecidesAvailable }` on a chats-v1
 * row (`groupNotifications`, the owner's row too; a joined member's `viewerState.notifications` as a fallback), on
 * the `members` answer (`yourNotifications`) and on the `viewer-state` answer (`viewerState.notifications`). Absent
 * from a symphony-bff that predates it → null, and nothing here is drawn.
 */

export type GroupNotificationMode = 'ALL' | 'MENTIONS_ONLY' | 'MAESTRO_DECIDES';
export const GROUP_NOTIFICATION_MODES: readonly GroupNotificationMode[] = ['ALL', 'MENTIONS_ONLY', 'MAESTRO_DECIDES'];

export interface GroupNotifications {
  mode: GroupNotificationMode;
  explicit: boolean;
  muted: boolean;
  maestroDecidesAvailable: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

export function parseGroupNotifications(raw: unknown): GroupNotifications | null {
  if (!isObject(raw) || !(GROUP_NOTIFICATION_MODES as readonly unknown[]).includes(raw.mode)) return null;
  return {
    mode: raw.mode as GroupNotificationMode,
    explicit: raw.explicit === true,
    muted: raw.muted === true,
    maestroDecidesAvailable: raw.maestroDecidesAvailable === true,
  };
}

/** A chats-v1 row: its own `groupNotifications` (owner and member), else a joined member's `viewerState.notifications`. */
export function notificationsFromRow(row: { groupNotifications?: unknown; viewerState?: unknown } | null | undefined): GroupNotifications | null {
  if (!row) return null;
  return (
    parseGroupNotifications(row.groupNotifications) ?? (isObject(row.viewerState) ? parseGroupNotifications(row.viewerState.notifications) : null)
  );
}

/** The mode "Use default" would apply: the shown one when it is already the default, else the server's rule. */
export function defaultMode(n: GroupNotifications): GroupNotificationMode {
  if (!n.explicit) return n.mode;
  return n.maestroDecidesAvailable ? 'MAESTRO_DECIDES' : 'ALL';
}

export function modeLabelKey(mode: GroupNotificationMode): string {
  return mode === 'MENTIONS_ONLY' ? 'chat.group.notify.mentions' : mode === 'MAESTRO_DECIDES' ? 'chat.group.notify.maestro' : 'chat.group.notify.all';
}

type T = (key: string, vars?: Record<string, string | number>) => string;

/** The value on the group info's "Notifications" row: Muted, else the mode, with "(default)" when not chosen. */
export function notificationsRowValue(n: GroupNotifications, t: T, maestroAgentName: string): string {
  if (n.muted) return t('chat.group.notify.muted');
  const label = t(modeLabelKey(n.mode), { maestroAgentName });
  return n.explicit ? label : t('chat.group.notify.withDefault', { mode: label });
}

export type NotificationChoice = { kind: 'mode'; mode: GroupNotificationMode } | { kind: 'default' } | { kind: 'mute'; muted: boolean };

export interface NotificationOption {
  choice: NotificationChoice;
  labelKey: string;
  /** A second line: the Maestro's ("Uses your credits"), or which default applies. */
  hint: { key: string; mode?: GroupNotificationMode } | null;
  checked: boolean;
}

/** The picker's choices, in order. "Let my Maestro decide" only while the caller's own Maestro is in the group. */
export function notificationOptions(n: GroupNotifications): NotificationOption[] {
  const modes = GROUP_NOTIFICATION_MODES.filter((m) => m !== 'MAESTRO_DECIDES' || n.maestroDecidesAvailable);
  return [
    ...modes.map(
      (mode): NotificationOption => ({
        choice: { kind: 'mode', mode },
        labelKey: modeLabelKey(mode),
        hint: mode === 'MAESTRO_DECIDES' ? { key: 'chat.group.notify.maestroHint' } : null,
        checked: n.explicit && n.mode === mode,
      }),
    ),
    {
      choice: { kind: 'default' },
      labelKey: 'chat.group.notify.useDefault',
      hint: { key: 'chat.group.notify.defaultIs', mode: defaultMode(n) },
      checked: !n.explicit,
    },
  ];
}

/** The `viewer-state` body for one choice. */
export function notificationWriteBody(conversationId: string, choice: NotificationChoice): Record<string, unknown> {
  if (choice.kind === 'mode') return { conversationId, notificationMode: choice.mode };
  if (choice.kind === 'default') return { conversationId, notificationModeDefault: true };
  return { conversationId, muted: choice.muted };
}

/** What the picker shows while the write is in flight. */
export function optimisticNotifications(n: GroupNotifications, choice: NotificationChoice): GroupNotifications {
  if (choice.kind === 'mode') return { ...n, mode: choice.mode, explicit: true };
  if (choice.kind === 'default') return { ...n, mode: defaultMode(n), explicit: false };
  return { ...n, muted: choice.muted };
}

/** A refused / failed write's message. */
export function notificationErrorKey(refusal: string | null | undefined): string {
  if (refusal === 'MAESTRO_NOT_IN_GROUP') return 'chat.group.notify.error.maestroNotInGroup';
  if (refusal === 'FEATURE_OFF') return 'chat.group.notify.error.featureOff';
  return 'chat.group.notify.error.failed';
}

// ─── The one-time "your Maestro now decides" hint ──────────────────────────────────────────────────────────────

/** localStorage, per device, cleared on sign-out; the phones keep the same set under the same name. */
export const MAESTRO_HINT_SEEN_KEY = 'symphony.groupNotify.maestroHintSeen';

/** Shown when the mode silently became "Maestro decides" (the default, with the caller's Maestro in), once per group. */
export function shouldShowMaestroDecidesHint(
  n: GroupNotifications | null,
  seen: ReadonlySet<string> | readonly string[],
  conversationId: string | null | undefined,
): boolean {
  if (!n || !conversationId) return false;
  const has = Array.isArray(seen) ? (seen as readonly string[]).includes(conversationId) : (seen as ReadonlySet<string>).has(conversationId);
  return n.mode === 'MAESTRO_DECIDES' && !n.explicit && n.maestroDecidesAvailable && !has;
}

export function readMaestroHintSeen(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MAESTRO_HINT_SEEN_KEY) ?? '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function markMaestroHintSeen(conversationId: string): void {
  if (typeof window === 'undefined' || !conversationId) return;
  const ids = readMaestroHintSeen();
  if (ids.includes(conversationId)) return;
  try {
    window.localStorage.setItem(MAESTRO_HINT_SEEN_KEY, JSON.stringify([...ids, conversationId].slice(-200)));
  } catch {
    // private mode / quota — the hint may then show again on a later visit
  }
}

/** Sign-out: the next account must not inherit this one's seen set. */
export function clearMaestroHintSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MAESTRO_HINT_SEEN_KEY);
  } catch {
    // nothing to clear
  }
}
